import React, { useState } from 'react';
import { Box } from '@material-ui/core';
import { Button } from '@monorepo/shared/componentsV2/Button';
import { Chip } from '@monorepo/shared/componentsV2/Chip';
import { DatePicker } from '@monorepo/shared/componentsV2/dateAndTimePickers';
import TrashIcon from '@svg/trash.svg';
import { localEquivalentOfUTC } from 'mapistry-shared';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { RadioGroup } from '@monorepo/shared/componentsV2/RadioGroup';
import {
  FieldWrapper,
  FormErrors,
  Modal,
  SaveButton,
} from '../../elements/index';
import AddAssignee from '../../elements/user/AddAssignee';
import {
  FormSaveStateType,
  InspectionAssignTaskType,
  UserType,
} from '../../propTypes';

const ModalSubtitle = styled('h5')`
  margin: 0 0 12px;
  font-size: large;
  font-weight: 400;
`;

const ChipList = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 1.25rem;
`;

const StyledChip = styled(Chip)`
  padding: 1rem 0.5rem;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.gray333};
`;

const DangerTrashIcon = styled(TrashIcon)`
  fill: ${({ theme }) => theme.colors.darkRed};
`;

function isValidDate(value) {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

const fmt = (date) => moment.utc(date).format('M/D/YYYY');

function formatRange(start, end) {
  const formattedStart = start ? fmt(start) : '';
  const formattedEnd = end ? fmt(end) : 'present';
  return `${formattedStart} - ${formattedEnd}`;
}

export function InspectionAssignModal(props) {
  const {
    assignees,
    currentlyAssigned,
    errors,
    isOpen,
    onChange,
    onClose,
    onRemoveAssignee,
    onSave,
    onStartDateChange,
    periodEnd,
    periodStart,
    saveState,
    startDate,
    task,
  } = props;

  const [isSaveAll, setIsSaveAll] = useState(true);

  const hasPeriod = !!periodStart;
  const hasDateError = startDate != null && !isValidDate(startDate);
  let startDateForPicker = null;
  if (startDate != null) {
    startDateForPicker = isValidDate(startDate)
      ? localEquivalentOfUTC(startDate)
      : startDate;
  }
  const startFormatted =
    startDate && isValidDate(startDate) ? fmt(startDate) : '';

  const footer = (
    <>
      <Button color="secondary" onClick={onClose}>
        Cancel
      </Button>
      <SaveButton
        disabled={hasDateError}
        label="Update Inspection"
        onSave={() => onSave(isSaveAll)}
        saveState={saveState}
      />
    </>
  );

  return (
    <Modal
      header={<h4>Assign &quot;{task.title}&quot;</h4>}
      footer={footer}
      fullWidth={false}
      maxWidth="xs"
      onClose={onClose}
      open={isOpen}
    >
      <ModalSubtitle>Choose a date range and assignee</ModalSubtitle>
      <Box minHeight={200}>
        {errors && (
          <Box mx={-3} mb={2}>
            <FormErrors errors={errors} />
          </Box>
        )}
        <DatePicker
          label="Start Date"
          required
          error={hasDateError}
          errorText={hasDateError ? 'Enter a valid date' : undefined}
          name="inspectionAssignmentStartDate"
          subText={
            hasPeriod
              ? `Inspection period: ${formatRange(periodStart, periodEnd)}`
              : ''
          }
          onChange={(e) => onStartDateChange(e.target.value)}
          value={startDateForPicker}
        />
        <RadioGroup
          label="End Date"
          onChange={(_, opt) => {
            setIsSaveAll(opt === 'all');
          }}
          required
          options={[
            {
              value: 'all',
              label: `All inspection periods during and after ${startFormatted}`,
            },
            {
              value: 'period',
              label: `Inspection period due date (${
                periodEnd ? fmt(periodEnd) : 'none'
              })`,
            },
          ]}
          value={isSaveAll ? 'all' : 'period'}
        />

        <FieldWrapper
          label={`Assigned as of ${startFormatted}`}
          subtext={currentlyAssigned.length ? undefined : 'No assignees'}
        >
          <ChipList>
            {currentlyAssigned.map((user) => (
              <StyledChip
                key={user.userId}
                label={user.name}
                deleteIcon={<DangerTrashIcon />}
                onDelete={() => onRemoveAssignee(user.userId)}
              />
            ))}
          </ChipList>
        </FieldWrapper>

        <FieldWrapper label="New assignee">
          <AddAssignee
            multi
            onChange={onChange}
            assignees={assignees}
            excludeUserIds={currentlyAssigned.map((user) => user.userId)}
          />
        </FieldWrapper>
      </Box>
    </Modal>
  );
}

InspectionAssignModal.propTypes = {
  assignees: PropTypes.arrayOf(UserType).isRequired,
  currentlyAssigned: PropTypes.arrayOf(UserType),
  errors: PropTypes.arrayOf(PropTypes.string),
  isOpen: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemoveAssignee: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onStartDateChange: PropTypes.func.isRequired,
  periodEnd: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  periodStart: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  saveState: FormSaveStateType.isRequired,
  startDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  task: InspectionAssignTaskType.isRequired,
};

InspectionAssignModal.defaultProps = {
  currentlyAssigned: [],
  errors: null,
  periodEnd: null,
  periodStart: null,
  startDate: null,
};
